// src/views/EmployeeProfilesView.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions'; // <--- NEW IMPORTS
import { Plus, CreditCard } from 'lucide-react'; // <--- Added CreditCard icon
import { roleLabel } from '../utils/permissions'; // leadhand-9cb21d86

const EmployeeProfilesView = ({ currentUserProfile, openEmployeeModal }) => {
    const [employees, setEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isPortalLoading, setIsPortalLoading] = useState(false); // <--- For the billing button

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;

        const orgId = currentUserProfile.organizationId;
        const usersRef = collection(db, 'users');
        
        // FILTER: Only show active users in THIS organization
        const q = query(
            usersRef,
            where("organizationId", "==", orgId),
            where("status", "in", ["active", "Active", "Manager", "Admin", "Owner/System Admin"]) 
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const employeeList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEmployees(employeeList);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching employee directory:", error);
            setIsLoading(false);
        });

        return unsubscribe;
    }, [currentUserProfile?.organizationId]);

    // --- NEW: HANDLE BILLING PORTAL (UPGRADE) ---
    const handleManageSubscription = async () => {
        setIsPortalLoading(true);
        try {
            const functions = getFunctions();
            const createPortalSession = httpsCallable(functions, 'createPortalSession');
            const { data } = await createPortalSession({ 
                returnUrl: window.location.href // Come back to this page after upgrading
            });
            
            if (data.url) {
                window.location.href = data.url;
            }
        } catch (error) {
            console.error("Failed to load billing portal:", error);
            alert("Could not load billing portal. Please try again.");
            setIsPortalLoading(false);
        }
    };

    // --- NEW: THE LIMIT CHECKER ---
    const handleAddClick = () => {
        // 1. Define Limits
        const LIMITS = { 'solo': 1, 'starter': 5, 'growth': 10, 'scale': 20 };
        
        // 2. Get Current Tier (Default to 'solo' if missing)
        const currentTier = currentUserProfile?.planTier || 'solo';
        const limit = LIMITS[currentTier] || 1;

        // 3. Check Count
        // We compare the current list length against the limit
        if (employees.length >= limit) {
            const confirmUpgrade = window.confirm(
                `Limit Reached!\n\nYour '${currentTier}' plan allows ${limit} users, and you currently have ${employees.length}.\n\nWould you like to upgrade your plan now?`
            );
            
            if (confirmUpgrade) {
                handleManageSubscription();
            }
            return; // STOP HERE. Do not open the modal.
        }

        // 4. If Pass, Open Modal
        openEmployeeModal();
    };

    if (isLoading) return <p className="text-center mt-8">Loading employee directory...</p>;

    return (
        <div className="p-6 md:p-10">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Employee Directory</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Plan: <span className="font-semibold uppercase">{currentUserProfile?.planTier || 'Solo'}</span> 
                        {' '}&bull;{' '}
                        Users: {employees.length} / {currentUserProfile?.planTier === 'scale' ? '20' : 
                            (currentUserProfile?.planTier === 'growth' ? '10' : 
                            (currentUserProfile?.planTier === 'starter' ? '5' : '1'))}
                    </p>
                </div>
                
                <div className="flex gap-3">
                    {/* --- NEW: MANAGE SUBSCRIPTION BUTTON --- */}
                    <button
                        onClick={handleManageSubscription}
                        disabled={isPortalLoading}
                        className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 transition-colors"
                    >
                        {isPortalLoading ? 'Loading...' : (
                            <>
                                <CreditCard size={18} className="mr-2" /> Manage Subscription
                            </>
                        )}
                    </button>

                    {/* --- UPDATED: ADD EMPLOYEE BUTTON --- */}
                    <button
                        onClick={handleAddClick} 
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 shadow-sm"
                    >
                        <Plus size={20} className="mr-2" /> Add Employee
                    </button>
                </div>
            </div>

            <div className="bg-white shadow-sm rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {employees.map(employee => (
                            <tr key={employee.id} onClick={() => openEmployeeModal(employee)} className="hover:bg-gray-50 cursor-pointer">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{employee.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{roleLabel(employee.role)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.phone || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${employee.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {employee.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {employees.length === 0 && !isLoading && (
                    <div className="text-center py-10 text-gray-500">
                        <p>No employees found.</p>
                        <p className="text-xs mt-2 text-gray-400">If you believe this is an error, check your database permissions.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmployeeProfilesView;